import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsRevenueBridgeResponse } from "@shared-core/data/api-types";
import { RevenueBridgeChart } from "./RevenueBridgeChart";

// The Revenue Bridge tab: loads the composed /views/insights/revenue-bridge
// bridge (server-classified + summed) and renders the margin-weighted cohort
// waterfall with its revenue/contribution toggle. Covers loading/empty/error,
// mirroring the other Insights tabs' template.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; bridge: InsightsRevenueBridgeResponse };

export function RevenueBridgeTab() {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const bridge = await adapter.getInsightsRevenueBridge();
      setState({ status: "ready", bridge });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading bridge…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load the revenue bridge. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return <RevenueBridgeChart bridge={state.bridge} />;
}
